import React, { useEffect, useRef, useState } from "react";
import * as S from "./Hero.styles";
import { IHeroProps } from "./Hero.types";
import { useMicrositeProductPage } from "@ubnt/store.core/lib/components/Microsites/context/MicrositeProductPageContext";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { useTabAutoplay } from "hooks/useTabAutoplay";
import { HeroVideoPlayInView } from "../HeroVideoPlayInView/HeroVideoPlayInView";
import { InView } from "react-intersection-observer";

const SINGLE_SECTION = 1;

export const Hero = ({
  title,
  subTitle,
  sections = [],
  shouldHideTabs,
  buyButton,
  children,
}: Readonly<React.PropsWithChildren<IHeroProps>>) => {
  const { showBuyButton } = useMicrositeProductPage();
  const container = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState(null);

  useTabAutoplay({
    activeTab,
    sections,
    shouldCancelUpdate: sections.length <= SINGLE_SECTION,
    setActiveTab,
  });

  useEffect(() => {
    setActiveTab(sections[0]);
  }, [sections]);

  useGSAP(
    () => {
      gsap.to(container.current, {
        opacity: 1,
        y: 0,
        duration: 0.75,
        delay: 0.1,
        ease: "power2.out",
      });
    },
    { dependencies: [], scope: container },
  );

  return (
    <S.HeroContainer>
      <S.Wrapper>
        <S.VideoGradientContainer>
          <InView
            as="div"
            threshold={0.1}
            trackVisibility
            delay={100}
            initialInView
          >
            {({ inView, ref }) => (
              <div ref={ref}>
                <HeroVideoPlayInView
                  section={activeTab}
                  totalSectionsCount={sections.length}
                  isInView={inView}
                />
              </div>
            )}
          </InView>

          <S.Gradient />
        </S.VideoGradientContainer>

        <S.Content ref={container}>
          <S.Title
            renderAs="h1"
            title={title ?? activeTab?.label}
            isCentered
            isMainTitle
            titleWeight={900}
            spacing={{ top: 32, bottom: 8 }}
          />
          {!!subTitle && <S.Subtitle>{subTitle}</S.Subtitle>}

          {!shouldHideTabs && (
            <S.TabContainer>
              {sections.length > SINGLE_SECTION &&
                sections.map((tab, index) => (
                  <S.Tab
                    key={`${tab.label}-${index}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    <S.StatusBarContainer>
                      <S.StyledStatusBar
                        $playbackLimit={tab.playbackTimeInSeconds}
                        $shouldAnimate={activeTab?.id === tab.id}
                      />
                    </S.StatusBarContainer>
                  </S.Tab>
                ))}
            </S.TabContainer>
          )}

          {showBuyButton && !!buyButton?.href && (
            <S.BuyButton href={buyButton.href} />
          )}

          {children ?? null}
        </S.Content>
      </S.Wrapper>

      {<S.GlobalInViewMonitorStyle />}
    </S.HeroContainer>
  );
};
